import React, { useState, useEffect, useCallback } from 'react';
import { Building2, RefreshCw, Search, PauseCircle, CheckCircle, Clock, Gauge } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function safeFetchJson(path, options = {}) {
  const url = `${BACKEND_URL}${path}`;
  const token =
    localStorage.getItem('superadmin_session') ||
    localStorage.getItem('superadmin_session_token') ||
    localStorage.getItem('superAdminToken') ||
    localStorage.getItem('superadminToken');

  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return fetch(url, { credentials: 'include', ...options, headers }).then((res) => {
    if (!res.ok) return res.text().then((t) => { throw new Error(`HTTP ${res.status}: ${t.slice(0, 200)}`); });
    return res.json();
  });
}

const STATUS_CONFIG = {
  active:    { color: 'bg-green-100 text-green-800',  icon: CheckCircle },
  trial:     { color: 'bg-blue-100 text-blue-800',    icon: Clock },
  suspended: { color: 'bg-red-100 text-red-800',      icon: PauseCircle },
  cancelled: { color: 'bg-gray-100 text-gray-700',    icon: PauseCircle },
};

function formatDate(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleDateString(); } catch { return ts; }
}

const BusinessManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}&limit=200` : '?limit=200';
      const data = await safeFetchJson(`/api/v1/super-admin/tenants${qs}`);
      setTenants(data.tenants || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const suspendTenant = async (tenantId, name) => {
    if (!window.confirm(`Suspend ${name}? This will disable their access.`)) return;
    setActionLoading(tenantId);
    try {
      await safeFetchJson(`/api/v1/super-admin/tenants/${tenantId}/suspend`, { method: 'POST' });
      toast({ title: 'Tenant suspended', description: `${name} has been suspended.` });
      await fetchTenants();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = tenants.filter((t) => {
    const q = search.toLowerCase();
    return !q ||
      (t.business_name || '').toLowerCase().includes(q) ||
      (t.brand_id || '').toLowerCase().includes(q) ||
      (t.industry || '').toLowerCase().includes(q) ||
      (t.sector || '').toLowerCase().includes(q);
  });

  const statusCounts = { active: 0, trial: 0, suspended: 0, cancelled: 0 };
  tenants.forEach((t) => { if (statusCounts[t.subscription_status] !== undefined) statusCounts[t.subscription_status]++; });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tenant Management</h1>
          <p className="text-gray-600 mt-1">Manage all businesses on the platform</p>
        </div>
        <Button onClick={fetchTenants} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(statusCounts).map(([status, count]) => {
          const cfg = STATUS_CONFIG[status] || {};
          const Icon = cfg.icon || Building2;
          return (
            <div key={status} className="bg-white rounded-xl shadow-sm p-4 border">
              <div className="flex items-center gap-2 mb-1">
                <Icon className="h-4 w-4 text-gray-400" />
                <span className="text-xs text-gray-500 capitalize">{status}</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{count}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search by name, brand ID or sector..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading && (
          <div className="p-8 text-center text-gray-500">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-400" />
            Loading tenants...
          </div>
        )}
        {error && !loading && (
          <div className="p-8 text-center">
            <p className="text-red-600 font-medium">{error}</p>
            <Button onClick={fetchTenants} className="mt-4" variant="outline">Retry</Button>
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>{search ? 'No tenants match your search.' : 'No tenants found.'}</p>
          </div>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Business</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Brand ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sector</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((tenant) => {
                const cfg = STATUS_CONFIG[tenant.subscription_status] || STATUS_CONFIG.active;
                const tenantId = tenant.id || tenant._id;
                return (
                  <tr key={tenantId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-xs">
                          {(tenant.business_name || '?').charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-900">{tenant.business_name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{tenant.brand_id || '—'}</td>
                    {/* CURRENT canonical industry (server-resolved from industry_id) when present, else
                        the legacy `sector` string. Shows the tenant's present industry after an owner
                        applies an Industry Profile. Backend preserves legacy sector for Tenant360. */}
                    <td className="px-4 py-3 text-gray-600" data-testid="tenant-sector">{tenant.industry || tenant.sector || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{tenant.subscription_plan_id || tenant.subscription_tier || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge className={cfg.color}>{tenant.subscription_status || '—'}</Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(tenant.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => navigate(`/superadmin-portal/dashboard/tenants/${encodeURIComponent(tenant.brand_id || tenant.business_id || tenantId)}/360`)}
                        >
                          <Gauge className="h-3 w-3 mr-1" />
                          Tenant 360
                        </Button>
                        {tenant.subscription_status !== 'suspended' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                            disabled={actionLoading === tenantId}
                            onClick={() => suspendTenant(tenantId, tenant.business_name)}
                          >
                            <PauseCircle className="h-3 w-3 mr-1" />
                            {actionLoading === tenantId ? '...' : 'Suspend'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
};

export default BusinessManagement;
